(function () {
  'use strict';

  if (!window.etUpdateMealsExtensions) {
    window.etUpdateMealsExtensions = {};
  }

  const extensions = window.etUpdateMealsExtensions;

  function getHelpers() {
    const getState = extensions.getState;
    const getPortalSession = extensions.getPortalSession;
    const getSubscriptions = extensions.getSubscriptions;
    const listCharges = extensions.listCharges;
    const setPreferredOverviewSubscriptionId =
      extensions.setPreferredOverviewSubscriptionId;

    if (
      typeof getState !== 'function' ||
      typeof getPortalSession !== 'function' ||
      typeof getSubscriptions !== 'function' ||
      typeof listCharges !== 'function' ||
      typeof setPreferredOverviewSubscriptionId !== 'function'
    ) {
      console.warn(
        '[Update Meals View Subscriptions] Required extension bridge helpers are unavailable.'
      );
      return null;
    }

    return {
      getState,
      getPortalSession,
      getSubscriptions,
      listCharges,
      setPreferredOverviewSubscriptionId,
    };
  }

  function parseInteger(value, fallback) {
    const sharedParser = window.etRechargeUtils?.parseInteger;
    if (typeof sharedParser === 'function') {
      return sharedParser(value, fallback);
    }
    const parsed = Number.parseInt(String(value ?? '').trim(), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return parsed;
  }

  function parseDateFromValue(rawValue) {
    const sharedParser = window.etRechargeUtils?.parseDateValue;
    if (typeof sharedParser === 'function') {
      return sharedParser(rawValue);
    }
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const year = parseInteger(isoMatch[1], Number.NaN);
      const monthIndex = parseInteger(isoMatch[2], Number.NaN) - 1;
      const day = parseInteger(isoMatch[3], Number.NaN);
      if (
        Number.isFinite(year) &&
        Number.isFinite(monthIndex) &&
        Number.isFinite(day)
      ) {
        const parsed = new Date(year, monthIndex, day);
        if (
          parsed.getFullYear() === year &&
          parsed.getMonth() === monthIndex &&
          parsed.getDate() === day
        ) {
          return parsed;
        }
      }
    }

    const timestamp = Date.parse(raw);
    if (!Number.isFinite(timestamp)) return null;
    const parsed = new Date(timestamp);
    if (!Number.isFinite(parsed.getTime())) return null;
    return parsed;
  }

  function resolveDeliveryDatePlusTwo(subscription, charge) {
    const sharedResolver = window.etRechargeUtils?.resolveChargeDateValue;
    const baseDate =
      typeof sharedResolver === 'function'
        ? sharedResolver(charge, subscription)
        : parseDateFromValue(
            charge && (charge.scheduled_at || charge.charge_date || charge.created_at)
          ) ||
          parseDateFromValue(subscription && subscription.next_charge_date) ||
          parseDateFromValue(subscription && subscription.next_charge_scheduled_at) ||
          parseDateFromValue(subscription && subscription.next_charge_at);
    if (!baseDate) return null;

    const deliveryDate = new Date(baseDate.getTime());
    deliveryDate.setDate(deliveryDate.getDate() + 2);
    return deliveryDate;
  }

  function resolveChargeTotalAmount(charge) {
    if (!charge || typeof charge !== 'object') return null;
    const candidates = [
      charge.total_price,
      charge.total,
      charge.total_price_including_tax,
      charge.total_price_incl_tax,
      charge.total_price_excluding_tax,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      const value = Number.parseFloat(
        typeof candidate === 'string' || typeof candidate === 'number'
          ? candidate
          : ''
      );
      if (Number.isFinite(value) && value >= 0) {
        return value;
      }
    }
    return null;
  }

  function resolveCurrencyCode(subscription, charge) {
    const candidates = [
      charge && (charge.currency || charge.currency_code),
      subscription && (subscription.currency || subscription.currency_code),
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = String(candidates[i] || '').trim();
      if (candidate) return candidate.toUpperCase();
    }
    return 'USD';
  }

  function formatMoney(amount, currencyCode) {
    if (!Number.isFinite(amount)) return '';
    try {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
      }).format(amount);
      return formatted.replace(/^US\$/, '$');
    } catch (error) {
      console.warn(
        '[Update Meals View Subscriptions] Failed to format money.',
        error
      );
      return `$${String(amount.toFixed ? amount.toFixed(2) : amount)}`;
    }
  }

  async function loadActiveSubscriptionsWithCharges() {
    const helpers = getHelpers();
    if (!helpers) return { active: [], summaries: [] };

    const { getPortalSession, getSubscriptions, listCharges } = helpers;

    const session = await getPortalSession();
    const subscriptions = await getSubscriptions(session);
    const safeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];

    const activeSubscriptions = safeSubscriptions.filter((entry) => {
      const status = String(entry && entry.status ? entry.status : '')
        .trim()
        .toLowerCase();
      return status === 'active';
    });

    if (activeSubscriptions.length < 2) {
      return { active: activeSubscriptions, summaries: [] };
    }

    let charges = [];
    try {
      const chargesResponse = await listCharges(session, {
        status: 'queued',
        limit: 250,
        sort_by: 'scheduled_at-asc',
      });
      charges = Array.isArray(chargesResponse)
        ? chargesResponse
        : (chargesResponse && chargesResponse.charges) || [];
    } catch (error) {
      console.warn(
        '[Update Meals View Subscriptions] Unable to load queued charges for summaries.',
        error
      );
    }

    function resolveNextChargeForSubscription(sub) {
      if (!Array.isArray(charges) || !charges.length) return null;

      if (sub.address_id) {
        const candidateCharges = charges.filter((charge) => {
          if (String(charge.address_id) !== String(sub.address_id)) return false;
          if (Array.isArray(charge.line_items)) {
            return charge.line_items.some(function (li) {
              return String(li.purchase_item_id) === String(sub.id);
            });
          }
          return false;
        });
        if (candidateCharges.length > 0) {
          candidateCharges.sort((a, b) => {
            const da = parseDateFromValue(
              a.scheduled_at || a.charge_date || a.created_at
            );
            const db = parseDateFromValue(
              b.scheduled_at || b.charge_date || b.created_at
            );
            const ta = da ? da.getTime() : Number.MAX_SAFE_INTEGER;
            const tb = db ? db.getTime() : Number.MAX_SAFE_INTEGER;
            return ta - tb;
          });
          return candidateCharges[0];
        }
      }

      const subCharges = charges.filter((charge) => {
        return String(charge.subscription_id) === String(sub.id);
      });
      if (subCharges.length > 0) {
        subCharges.sort((a, b) => {
          const da = parseDateFromValue(
            a.scheduled_at || a.charge_date || a.created_at
          );
          const db = parseDateFromValue(
            b.scheduled_at || b.charge_date || b.created_at
          );
          const ta = da ? da.getTime() : Number.MAX_SAFE_INTEGER;
          const tb = db ? db.getTime() : Number.MAX_SAFE_INTEGER;
          return ta - tb;
        });
        return subCharges[0];
      }

      return null;
    }

    const summaries = activeSubscriptions.map((subscription) => {
      const nextCharge = resolveNextChargeForSubscription(subscription);
      const deliveryDate = resolveDeliveryDatePlusTwo(subscription, nextCharge);
      const currencyCode = resolveCurrencyCode(subscription, nextCharge);
      const totalAmount = resolveChargeTotalAmount(nextCharge);
      const totalFormatted =
        totalAmount !== null && totalAmount !== undefined
          ? formatMoney(totalAmount, currencyCode)
          : '';

      return {
        subscriptionId: subscription.id,
        subscription,
        nextCharge,
        deliveryDate,
        currencyCode,
        totalAmount,
        totalFormatted,
      };
    });

    return { active: activeSubscriptions, summaries };
  }

  let modalOverlay = null;
  let modalKeydownHandler = null;
  let lastModalTrigger = null;

  function getFocusableElements(container) {
    if (!container) return [];
    const candidates = Array.prototype.slice.call(
      container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );
    return candidates.filter(function (el) {
      if (!(el instanceof HTMLElement)) return false;
      if (el.hasAttribute('disabled')) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      return true;
    });
  }

  function ensureModal() {
    if (modalOverlay) return modalOverlay;

    modalOverlay = document.createElement('div');
    modalOverlay.className = 'et-update-meals-subscription-modal-overlay';
    modalOverlay.setAttribute('role', 'dialog');
    modalOverlay.setAttribute('aria-modal', 'true');
    modalOverlay.setAttribute(
      'aria-label',
      'Switch subscription for next order'
    );

    modalOverlay.innerHTML = [
      '<div class="et-update-meals-subscription-modal" data-role="subscription-modal" tabindex="-1">',
      '  <div class="et-update-meals-subscription-modal__header">',
      '    <h2 class="et-update-meals-subscription-modal__title">Switch subscription</h2>',
      '    <button type="button" class="et-update-meals-subscription-modal__close" data-role="close-subscription-modal" aria-label="Close">&times;</button>',
      '  </div>',
      '  <div class="et-update-meals-subscription-modal__body">',
      '    <p class="et-update-meals-subscription-modal__subtitle">Choose which subscription you want to manage.</p>',
      '    <div class="et-update-meals-subscription-modal__list" data-role="subscription-list"></div>',
      '    <div class="et-update-meals-subscription-modal__actions">',
      '      <button type="button" class="et-update-meals-subscription-modal__button et-update-meals-subscription-modal__button--ghost" data-role="cancel-subscription-modal">Cancel</button>',
      '    </div>',
      '  </div>',
      '</div>',
    ].join('');

    modalOverlay.addEventListener('click', function (event) {
      if (event.target === modalOverlay) {
        closeModal();
      }
    });

    const closeButton = modalOverlay.querySelector(
      '[data-role="close-subscription-modal"]'
    );
    const cancelButton = modalOverlay.querySelector(
      '[data-role="cancel-subscription-modal"]'
    );
    if (closeButton) {
      closeButton.addEventListener('click', function () {
        closeModal();
      });
    }
    if (cancelButton) {
      cancelButton.addEventListener('click', function () {
        closeModal();
      });
    }

    document.body.appendChild(modalOverlay);
    return modalOverlay;
  }

  function openModal(trigger) {
    const overlay = ensureModal();
    const modal = overlay.querySelector('[data-role="subscription-modal"]');

    lastModalTrigger =
      trigger instanceof HTMLElement
        ? trigger
        : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    overlay.style.display = 'flex';

    const focusable = getFocusableElements(modal || overlay);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (modal instanceof HTMLElement) {
      modal.focus();
    }

    if (modalKeydownHandler) {
      if (modal instanceof HTMLElement) {
        modal.removeEventListener('keydown', modalKeydownHandler);
      }
      modalKeydownHandler = null;
    }

    if (modal instanceof HTMLElement) {
      modalKeydownHandler = function (event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.preventDefault();
          closeModal();
          return;
        }

        if (event.key !== 'Tab') {
          return;
        }

        const focusableEls = getFocusableElements(modal);
        if (!focusableEls.length) {
          return;
        }

        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];
        const activeElement = document.activeElement;
        const isShift = event.shiftKey;

        if (!isShift && activeElement === lastEl) {
          event.preventDefault();
          firstEl.focus();
        } else if (isShift && activeElement === firstEl) {
          event.preventDefault();
          lastEl.focus();
        }
      };

      modal.addEventListener('keydown', modalKeydownHandler);
    }
  }

  function closeModal() {
    if (!modalOverlay) return;
    modalOverlay.style.display = 'none';

    const modal = modalOverlay.querySelector('[data-role="subscription-modal"]');
    if (modal instanceof HTMLElement && modalKeydownHandler) {
      modal.removeEventListener('keydown', modalKeydownHandler);
      modalKeydownHandler = null;
    }

    if (lastModalTrigger && document.contains(lastModalTrigger)) {
      try {
        lastModalTrigger.focus();
      } catch (e) {
        // ignore focus errors
      }
    }
  }

  async function handleViewOtherSubscriptionsClick(event) {
    const trigger =
      event.target instanceof Element
        ? event.target.closest('[data-action="et-view-other-subscriptions"]')
        : null;
    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    if (
      trigger.getAttribute('aria-disabled') === 'true' ||
      trigger.getAttribute('data-et-disabled') === 'true' ||
      trigger.disabled ||
      trigger.hasAttribute('disabled')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    try {
      const helpers = getHelpers();
      if (!helpers) return;

      const { getState, setPreferredOverviewSubscriptionId } = helpers;

      const { active, summaries } = await loadActiveSubscriptionsWithCharges();
      if (!Array.isArray(active) || active.length < 2) {
        return;
      }

      const state = typeof getState === 'function' ? getState() : null;
      const currentId =
        state && state.overviewSlotSubscriptionId
          ? state.overviewSlotSubscriptionId
          : null;

      const overlay = ensureModal();
      const listEl = overlay.querySelector('[data-role="subscription-list"]');
      if (!listEl) return;

      listEl.innerHTML = '';

      summaries.forEach(function (summary) {
        const subscriptionId = summary.subscriptionId;
        const isActive =
          currentId &&
          String(currentId) === String(subscriptionId != null ? subscriptionId : '');

        let displayText = 'Subscription';
        const parts = [];
        
        if (summary.deliveryDate instanceof Date) {
          try {
            const dayOfWeek = new Intl.DateTimeFormat(undefined, {
              weekday: 'long',
            }).format(summary.deliveryDate);
            parts.push(dayOfWeek);
          } catch (formatError) {
          }
        }
        
        if (summary.totalFormatted) {
          parts.push(summary.totalFormatted);
        }
        
        if (parts.length > 0) {
          displayText = `${displayText} - ${parts.join(' - ')}`;
        }

        const item = document.createElement('button');
        item.type = 'button';
        item.className =
          'et-update-meals-subscription-modal__item' +
          (isActive
            ? ' et-update-meals-subscription-modal__item--active'
            : '');
        item.setAttribute('data-subscription-id', String(subscriptionId));
        item.setAttribute('data-role', 'subscription-option');
        item.innerHTML =
          '<div class="et-update-meals-subscription-modal__item-text">' +
          '<div class="et-update-meals-subscription-modal__item-title">' +
          displayText +
          '</div>' +
          '</div>' +
          (isActive
            ? '<div class="et-update-meals-subscription-modal__item-indicator" aria-hidden="true">\u2713</div>'
            : '');

        item.addEventListener('click', function () {
          if (!subscriptionId) return;
          setPreferredOverviewSubscriptionId(subscriptionId);
          closeModal();
          document.dispatchEvent(
            new CustomEvent('ET::view-other-subscriptions::changed')
          );
          document.dispatchEvent(new CustomEvent('Affinity:refresh'));
        });

        listEl.appendChild(item);
      });

      openModal(trigger);
    } catch (error) {
      console.warn(
        '[Update Meals View Subscriptions] Failed to open subscription selector modal.',
        error
      );
    }
  }

  async function refreshTriggerVisibility() {
    try {
      const trigger = document.querySelector(
        '[data-action="et-view-other-subscriptions"]'
      );
      if (!(trigger instanceof HTMLElement)) {
        return;
      }

      const helpers = getHelpers();
      if (!helpers) return;

      const { getPortalSession, getSubscriptions } = helpers;
      const session = await getPortalSession();
      const subscriptions = await getSubscriptions(session);
      const safeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];
      const activeSubscriptions = safeSubscriptions.filter((entry) => {
        const status = String(entry && entry.status ? entry.status : '')
          .trim()
          .toLowerCase();
        return status === 'active';
      });

      const shouldShow = activeSubscriptions.length > 1;
      trigger.style.display = shouldShow ? '' : 'none';
      trigger.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    } catch (error) {
      console.warn(
        '[Update Meals View Subscriptions] Failed to refresh trigger visibility.',
        error
      );
    }
  }

  let listenersInstalled = false;

  function installViewOtherSubscriptionsListeners() {
    if (listenersInstalled) return;
    listenersInstalled = true;

    document.addEventListener('click', handleViewOtherSubscriptionsClick, true);

    document.addEventListener(
      'Recharge::slot::mounted',
      function () {
        refreshTriggerVisibility();
      },
      true
    );

    document.addEventListener(
      'Affinity:refresh',
      function () {
        refreshTriggerVisibility();
      },
      true
    );

    document.addEventListener(
      'Recharge::location::change',
      function () {
        closeModal();
      },
      true
    );
  }

  extensions.viewOtherSubscriptions = {
    installListeners: installViewOtherSubscriptionsListeners,
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      installViewOtherSubscriptionsListeners
    );
  } else {
    installViewOtherSubscriptionsListeners();
  }
})();
